import { Component, computed, inject, input, signal } from "@angular/core";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import { faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";

import { TranslateTokenPipe } from "../../../../core/pipes";
import { AdaaHelper, isDsV2 } from "../../../../core/utils";
import { Constants } from "../../../constants/constants";
import { DataEntryDetails } from "../../../models";

export type DataEntryDetailBadge = {
  token?: string;
  key?: string;
};

export type DataEntryDetailField = {
  label: string;
  token: string;
  fullWidth?: boolean;
};

@Component({
  selector: "adaa-data-entry-details",
  imports: [TranslatePipe, TranslateTokenPipe, FontAwesomeModule],
  templateUrl: "./data-entry-details.html",
  styleUrl: "./data-entry-details.scss",
})
export class DataEntryDetailsComponent {
  private _translateService = inject(TranslateService);

  dataEntryDetails = input.required<DataEntryDetails>();
  itemType = input<"kpi" | "activity" | "benchmark">("kpi");

  readonly isExpanded = signal<boolean>(true);

  isKpi = computed(() => this.itemType() === "kpi");
  isActivity = computed(() => this.itemType() === "activity");
  isBenchmark = computed(() => this.itemType() === "benchmark");

  typeToken = computed(() => (this.isActivity() ? "typeName" : this.isKpi() ? "kpiType" : "kpiTypeName"));

  toggleIcon = computed(() => (this.isExpanded() ? faChevronUp : faChevronDown));

  hasNoFormula = computed<boolean>(() => this.dataEntryDetails().formula === Constants.FORMULA_STRING.NOFORMULA);

  badges = computed<DataEntryDetailBadge[]>(() => {
    const details = this.dataEntryDetails();
    const badges: DataEntryDetailBadge[] = [];

    if (this._hasToken(details, this.typeToken())) badges.push({ token: this.typeToken() });
    if (this._hasToken(details, "frequency")) badges.push({ token: "frequency" });
    if (this.hasNoFormula()) badges.push({ key: "kpi.NOFORMULAV" });

    return badges;
  });

  fields = computed<DataEntryDetailField[]>(() => {
    const details = this.dataEntryDetails();

    const candidates: DataEntryDetailField[] = [
      { label: "data_entry.description", token: "dsc", fullWidth: true },
      { label: "data_entry.linked_to", token: "linkedKpiType" },
      { label: "kpi.track", token: "trackName" },
      { label: "ai_projects.tracks.entry_classification", token: "entryClassificationName" },
      { label: "data_entry.pillar_name", token: "pillarName" },
      { label: "mtkpi.projectMilestoneId", token: "nationalProjectMilestoneName" },
      { label: "data_entry.objective_name", token: "objectiveName" },
      { label: this._initiativeLabel(details.initiativeType), token: "initiativeName" },
      { label: "data_entry.activity_name", token: "activityName" },
      { label: "data_entry.govdir", token: "subGovDirectionName" },
      { label: "data_entry.mainService_name", token: "mainServiceName" },
      { label: "data_entry.parent_KPI", token: "parentKpiName" },
      { label: "data_entry.sponsor", token: "sponsor" },
      { label: "data_entry.sponsor_other", token: "sponsorOther" },
    ];

    if (details.kpiType === Constants.KPI_TYPE.MOKPI) {
      candidates.push({ label: "national_projects.singular_title", token: "nationalProjectName" });
    }

    if (details.kpiType === Constants.KPI_TYPE.NTKPI) {
      candidates.push({ label: "directions.sub_pillar", token: "auxDirectionName" });
    }

    if (this.isActivity()) {
      candidates.push({ label: "data_entry.project_name", token: "projectName" });
    }

    return candidates.filter((field) => this._hasToken(details, field.token));
  });

  formula = computed<string>(() => {
    let formula = this.dataEntryDetails().formula ?? "";

    if (formula === Constants.FORMULA_STRING.NOFORMULA) formula = this._translateService.instant("kpi.NOFORMULAV");
    else {
      formula = this.dataEntryDetails().formula
        ? `<span class="keep-to-left">${this.dataEntryDetails().formula}</span>`
        : "";

      this.dataEntryDetails().metricDetails?.forEach((metric) => {
        formula += `<br /> ${AdaaHelper.getItemValueByToken(metric, "metricName")} - [ ${this._getMetricType(
          metric.metricType
        )} ] - ${AdaaHelper.getItemValueByToken(metric, "dsc")}`;
      });
    }

    return formula;
  });

  readonly Constants = Constants;
  readonly isDsV2 = () => isDsV2();

  readonly initiativeTypeText = (type: number | undefined) => this._initiativeLabel(type);

  public toggleDetails(): void {
    this.isExpanded.update((expanded) => !expanded);
  }

  private _initiativeLabel(type: number | undefined): string {
    if (!this.isDsV2()) return "initiatives.title";

    return type === Constants.INITIATIVE_TYPES.REGULAR_TASK
      ? "initiatives.regular_task"
      : "initiatives.strategic_project";
  }

  private _hasToken(details: DataEntryDetails, token: string): boolean {
    const record = details as Record<string, unknown>;

    return (
      AdaaHelper.isDefinedAndNotEmpty(record[`${token}AE`]) || AdaaHelper.isDefinedAndNotEmpty(record[`${token}EN`])
    );
  }

  private _getMetricType(type: string | undefined): string {
    switch (type) {
      case Constants.METRICS_TYPE.GLOBAL:
        return this._translateService.instant("main_types.G");
      case Constants.METRICS_TYPE.LOCAL:
        return this._translateService.instant("main_types.L");
      case Constants.METRICS_TYPE.DIMENSION:
        return this._translateService.instant("main_types.D");
      default:
        return this._translateService.instant("metrics.custom_var");
    }
  }
}
